import {Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import template from './index.jade';
import css from './index.styl?inline';
import {IntroStepName, IntroVersion, V2_STEPS} from './constants';
import {IntroState, IntroStep} from './types';
import './event-carousel';

const IntroStepTrackingLabel = {
  [IntroStepName.Mixpanel]: `Mixpanel`,
  [IntroStepName.Users]: `Users`,
  [IntroStepName.Events]: `Events`,
  [IntroStepName.Profiles]: `Profiles`,
  [IntroStepName.Complete]: `Complete`,
};

export class Intro extends Component<IntroState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'first-name': `string`,
      next: `string`,
      'is-dashboards-next': `boolean`,
      'is-full-page': `boolean`,
    };
  }

  get config(): ConfigOptions<IntroState> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        step: 0,
        visited: new Set(),
      },
      helpers: {
        IntroStepName,
        IntroVersion,
        getIntroSteps: (): IntroStep[] => {
          return this.getIntroSteps();
        },
        handleStepClick: (step: number): void => {
          if (step !== this.state.step) {
            this.changeStep(step);
          }
        },
        nextStep: (): void => {
          this.changeStep(this.state.step + 1);
        },
        appendMpSource: (source: string): string => {
          if (!this.attr(`is-full-page`)) {
            return null;
          }
          const url = new URL(this.attr(`next`), window.location.href);
          url.searchParams.append(`mp_source`, source);
          return url.href;
        },
        onCloseButtonClick: (): void => {
          this.dispatchEvent(new CustomEvent(`close`));
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.trackStep(this.state.step);
  }

  changeStep(nextStep: number): void {
    const {visited, step: currentStep} = this.state;
    this.update({
      step: Math.min(nextStep, this.numSteps - 1),
      visited: visited.add(currentStep),
    });

    this.trackStep(this.state.step);
    if (visited.size === this.numSteps - 1) {
      mixpanel.people.set_once({'Completed onboarding intro version': IntroVersion.V2});
    }
  }

  private getIntroSteps() {
    return V2_STEPS.map((step: IntroStep, index) => ({
      ...step,
      active: this.state.step === index,
      visited: this.state.visited.has(index),
    }));
  }

  private trackStep(step) {
    const stepName = this.getIntroSteps()[step].name;
    this.track(`Viewed Intro Step`, {'step index': step, 'step name': IntroStepTrackingLabel[stepName]});
  }

  private track(eventName, props = {}) {
    mixpanel.track(`[Onboarding] ${eventName}`, {
      ...props,
      'is full page': this.attr(`is-full-page`),
      version: IntroVersion.V2,
    });
  }

  private get numSteps() {
    return V2_STEPS.length;
  }
}

export const IntroEl = defineAndMakeJsxFactory(`mp-intro`, Intro);
